import { ContactStatusEnum, IContactRespSchema } from '@monetize/types/app';
import { createColumnHelper } from '../../../components/Monetize/DataTableNew/appTable';
import { MHStack, MTag, MText, MVStack } from '../../../components/Monetize';
import { actionCatalogColumn } from '../../../components/Monetize/DataTableNew/columnCatalog/structuralColumns';
import {
  ColumnCatalog,
  ColumnContextBase,
} from '../../../components/Monetize/DataTableNew/columnCatalog/types';
import {
  dateTimeBodyTemplate,
  enumDisplayBodyTemplate,
  getColHeaderFilters,
  getColHeaderWithSize,
  getColumnFilterValuesForEnum,
  nameWithIdBodyTemplate,
  textBodyTemplate,
} from '../../../components/Monetize/DataTableNew/tableUtilsNew';
import { CONTACT_STATUS_DISPLAY } from '../../../constants/contacts';
import { getAddress } from '../../../utils/address';

const columnHelper = createColumnHelper<IContactRespSchema>();

export interface ContactListColumnContext extends ColumnContextBase<IContactRespSchema> {
  /**
   * Route for a contact's detail page. Owned by the list so the Name link and
   * the row click resolve edit vs. view through the same ACL check.
   */
  getContactRoute: (contactId: string) => string;
}

/** tableId: local filter state (no FilterStateKeys) */
export const CONTACT_LIST_CATALOG: ColumnCatalog<
  IContactRespSchema,
  ContactListColumnContext
> = [
  {
    id: 'fullName',
    label: 'Name',
    customizable: false,
    build: ({ getContactRoute }) =>
      columnHelper.accessor('fullName', {
        ...getColHeaderWithSize('Name', 250),
        ...getColHeaderFilters('TEXT'),
        cell: (rowData) => {
          const contact = rowData.row.original;
          return (
            <MHStack>
              {nameWithIdBodyTemplate<IContactRespSchema, unknown>({
                idProp: 'id',
                nameProp: 'fullName',
                idLinkFn: getContactRoute,
              })(rowData)}
              {contact.primary && (
                <MTag variant="blue" ml="3">
                  Primary
                </MTag>
              )}
            </MHStack>
          );
        },
        enableSorting: true,
      }),
  },
  {
    id: 'email',
    label: 'Email',
    build: () =>
      columnHelper.accessor('email', {
        ...getColHeaderWithSize('Email', 250),
        ...getColHeaderFilters('TEXT'),
        cell: textBodyTemplate(),
        enableSorting: false,
      }),
  },
  {
    id: 'address',
    label: 'Address',
    build: () =>
      columnHelper.display({
        id: 'address',
        ...getColHeaderWithSize('Address', 250),
        cell: ({ row }) => {
          const contact = row.original;
          if (!contact.address) {
            return null;
          }
          const { cityStatePCCountry } = getAddress(contact.address);
          return (
            <MVStack alignItems="flex-start" gap="0.2rem">
              {contact.address.line1 && (
                <MText lineClamp={1}>{contact.address.line1}</MText>
              )}
              {contact.address.line2 && (
                <MText lineClamp={1}>{contact.address.line2}</MText>
              )}
              <MText lineClamp={1}>{cityStatePCCountry}</MText>
            </MVStack>
          );
        },
        enableSorting: false,
      }),
  },
  {
    id: 'status',
    label: 'Status',
    build: () =>
      columnHelper.accessor('status', {
        ...getColHeaderWithSize('Status', 150),
        ...getColHeaderFilters('SET', {
          predefinedFilterValues: getColumnFilterValuesForEnum(
            ContactStatusEnum,
            CONTACT_STATUS_DISPLAY,
          ),
        }),
        cell: enumDisplayBodyTemplate({
          displayMap: CONTACT_STATUS_DISPLAY,
        }),
        enableSorting: true,
      }),
  },
  {
    id: 'modifyDate',
    label: 'Updated',
    build: () =>
      columnHelper.accessor('modifyDate', {
        ...getColHeaderWithSize('Updated', 170),
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  {
    id: 'createDate',
    label: 'Created',
    build: () =>
      columnHelper.accessor('createDate', {
        ...getColHeaderWithSize('Created', 170),
        ...getColHeaderFilters('DATE'),
        cell: dateTimeBodyTemplate(),
        enableSorting: true,
      }),
  },
  actionCatalogColumn<IContactRespSchema>(),
];
